import { TooltipContentProps, TooltipPortal } from '@radix-ui/react-tooltip';
import { ReactNode } from 'react';
import { Tooltip, TooltipContent, TooltipTrigger } from 'src/shared/components/ui/alerts/tooltip';
import { cn } from 'src/shared/lib/utils';

type WithTooltipProps = {
  align?: 'center' | 'end' | 'start';
  side?: 'top' | 'right' | 'bottom' | 'left';
  tooltip: ReactNode;
  delay?: number;
  children: ReactNode;
  disabled?: boolean;
} & TooltipContentProps;

/**
 * A wrapper component that adds tooltip functionality to its children.
 * @warning Children should not be a button element as this will cause hydration errors
 * @note Uses the TooltipProvider from RootProviders.tsx - do not wrap this in another TooltipProvider
 */
const WithTooltip = ({
  children,
  tooltip,
  align,
  side,
  delay = 100,
  sideOffset,
  disabled,
  ...tooltipContentProps
}: WithTooltipProps) => {
  if (!tooltip) {
    return children;
  }

  return (
    <Tooltip delayDuration={delay}>
      <TooltipTrigger className={cn('select-none', '!pointer-events-auto')} disabled={disabled} asChild>
        {children}
      </TooltipTrigger>
      {!disabled && (
        <TooltipPortal>
          <TooltipContent
            className="max-w-xs select-none normal-case font-normal text-xs whitespace-normal text-wrap break-words"
            align={align}
            side={side}
            sideOffset={sideOffset}
            {...tooltipContentProps}
          >
            {tooltip}
          </TooltipContent>
        </TooltipPortal>
      )}
    </Tooltip>
  );
};

WithTooltip.displayName = 'WithTooltip';

export default WithTooltip;
